import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import { InspectionsIcon } from 'assets/icons';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentRibbon from 'components/foundation/dashboard/DashboardContentRibbon/DashboardContentRibbon';
import DashboardRibbonGroup from 'components/foundation/dashboard/DashboardRibbonGroup/DashboardRibbonGroup';
import { useDashboardTimeRange } from 'containers/dashboard/hooks/useDashboardTimeRange';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import { convertDashboardTimeFrameToTimeRangeType, getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import useEquipmentInspectionsNavigation from 'hooks/navigation/useEquipmentInspectionsNavigation';
import { t } from 'i18next';
import { useFeatureFlags } from 'utils/features/hooks';
import DashboardProUpgradeCard from '../DashboardProUpgradeCard/DashboardProUpgradeCard';
import useEquipmentInspectionCounts from './hooks/useEquipmentInspectionCounts';

export interface IEquipmentInspectionsCardProps {
  className?: ClassName;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide?: () => void;
}

enum OptionsKey {
  SATISFACTORY = 'satisfactory',
  NEEDS_ATTENTION = 'needsAttention',
  UNSATISFACTORY = 'unsatisfactory',
}

const EquipmentInspectionsCard = (props: IEquipmentInspectionsCardProps) => {
  const { className, theme, setting, onHide } = props;

  const currentTimeFrame: DashboardTimeFrame = setting.options?.time ?? 'payPeriod';
  const timeRange = useDashboardTimeRange(currentTimeFrame, 'utc');
  const equipmentInspectionsNavigation = useEquipmentInspectionsNavigation();
  const isPro = useFeatureFlags('PRO');

  const { satisfactory, needsAttention, unsatisfactory, loading } = useEquipmentInspectionCounts(timeRange, isPro);
  const totals = {
    satisfactory: satisfactory.toString(),
    needsAttention: needsAttention.toString(),
    unsatisfactory: unsatisfactory.toString(),
  };

  const onCardSelection = (key: OptionsKey) => {
    equipmentInspectionsNavigation({
      start_date: timeRange.startTime.toISODate(),
      end_date: timeRange.endTime.toISODate(),
      time_range_type: convertDashboardTimeFrameToTimeRangeType(currentTimeFrame),
      equipment_condition: key,
    });
  };

  const subtitle = getDashboardTimeFrameTitle(t('Equipment Inspections'), currentTimeFrame) ?? '';

  const classes = classNames('equipment-inspections-card', className);

  return (
    <>
      {isPro ? (
        <DashboardRibbonGroup className={classes}>
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => onCardSelection(OptionsKey.SATISFACTORY)}
            title={t('Satisfactory')}
            subtitle={subtitle}
            value={totals.satisfactory}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => onCardSelection(OptionsKey.NEEDS_ATTENTION)}
            title={t('Needs Attention')}
            subtitle={subtitle}
            value={totals.needsAttention}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => onCardSelection(OptionsKey.UNSATISFACTORY)}
            title={t('Unsatisfactory')}
            subtitle={subtitle}
            value={totals.unsatisfactory}
            isLoading={loading}
          />
        </DashboardRibbonGroup>
      ) : (
        <DashboardProUpgradeCard title={setting.title} svg={InspectionsIcon} theme={theme} onHide={onHide} />
      )}
    </>
  );
};

export default EquipmentInspectionsCard;
